/**
 * Expenses module hooks
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export type ExpenseStatus =
  | 'pending_invoice' | 'pending_allocation' | 'pending_approval'
  | 'approved' | 'rejected' | 'cancelled';

export type ExpenseSource =
  | 'sifi_card' | 'other_card' | 'company_cash' | 'driver_claim'
  | 'bank_transfer' | 'petroapp' | 'manual' | 'zoho_pull';

export type SyncStatus =
  | 'local_only' | 'pending_push' | 'pushing' | 'synced'
  | 'pending_pull' | 'conflict' | 'error';

export interface Expense {
  id: string;
  code: string;
  source: ExpenseSource;
  expense_date: string;
  amount_sar: string;
  vat_amount_sar: string | null;
  description: string | null;
  category_id: string | null;
  category_code: string | null;
  category_name_en: string | null;
  vendor_name_raw: string | null;
  vehicle_id: string | null;
  vehicle_plate: string | null;
  investor_id: string | null;
  site_id: string | null;
  site_name: string | null;
  trip_id: string | null;
  trip_code: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  receipt_photo_url: string | null;
  status: ExpenseStatus;
  legal_entity_id: string | null;
  legal_entity_code: string | null;
  zoho_record_type: string | null;
  zoho_record_id: string | null;
  sync_status: SyncStatus;
  is_reconciled: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  rejection_reason: string | null;
}

export interface ExpenseCategory {
  id: string;
  code: string;
  name_en: string;
  name_ar: string | null;
  parent_id: string | null;
  is_vat_eligible: boolean;
}

export interface ListFilters {
  dateFrom?: string;
  dateTo?: string;
  status?: ExpenseStatus;
  syncStatus?: SyncStatus;
  categoryId?: string;
  vehicleId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

// ─── List ───────────────────────────────────────────────────────
export function useExpenses(filters: ListFilters) {
  return useQuery({
    queryKey: ['expenses', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.status) params.set('status', filters.status);
      if (filters.syncStatus) params.set('syncStatus', filters.syncStatus);
      if (filters.categoryId) params.set('categoryId', filters.categoryId);
      if (filters.vehicleId) params.set('vehicleId', filters.vehicleId);
      if (filters.search) params.set('search', filters.search);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
      const { data } = await apiClient.get<{
        data: { items: Expense[]; total: number; page: number; pageSize: number };
      }>(`/api/v1/expenses?${params}`);
      return data.data;
    },
  });
}

// ─── Categories ─────────────────────────────────────────────────
export function useExpenseCategories() {
  return useQuery({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: ExpenseCategory[] }>(
        '/api/v1/expenses/categories'
      );
      return data.data;
    },
  });
}

// ─── Get one ────────────────────────────────────────────────────
export function useExpense(id: string | null) {
  return useQuery({
    queryKey: ['expense', id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await apiClient.get<{ data: Expense }>(`/api/v1/expenses/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

// ─── Create ─────────────────────────────────────────────────────
export interface CreateExpenseInput {
  expenseDate: string;
  amountSar: number;
  vatAmountSar?: number;
  source: ExpenseSource;
  categoryId?: string | null;
  vendorNameRaw?: string | null;
  vehicleId?: string | null;
  investorId?: string | null;
  siteId?: string | null;
  tripId?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  description?: string | null;
  notes?: string | null;
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateExpenseInput) => {
      const { data } = await apiClient.post<{ data: Expense }>('/api/v1/expenses', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}

// ─── Update ─────────────────────────────────────────────────────
export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: CreateExpenseInput & { id: string }) => {
      const { data } = await apiClient.patch<{ data: Expense }>(`/api/v1/expenses/${id}`, input);
      return data.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense', vars.id] });
    },
  });
}

// ─── Approve ────────────────────────────────────────────────────
export function useApproveExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const { data } = await apiClient.post<{ data: Expense }>(
        `/api/v1/expenses/${id}/approve`, { notes }
      );
      return data.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense', vars.id] });
    },
  });
}

// ─── Reject ─────────────────────────────────────────────────────
export function useRejectExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data } = await apiClient.post<{ data: Expense }>(
        `/api/v1/expenses/${id}/reject`, { reason }
      );
      return data.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense', vars.id] });
    },
  });
}

// ─── Delete ─────────────────────────────────────────────────────
export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/expenses/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}
